<template>
  <article class="rounded-[22px] border border-[#E3ECFF] dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-[0_16px_48px_rgba(57,64,79,0.08)] dark:shadow-none">
    <div class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-3">
          <div class="flex size-12 items-center justify-center rounded-[16px] bg-[#E7F0FF] dark:bg-slate-800 text-sm font-bold text-[#304D8A] dark:text-slate-200 font-manrope">
            {{ numberInitials }}
          </div>
          <div class="min-w-0">
            <h6 class="truncate text-lg font-bold text-[#182233] dark:text-slate-100 font-manrope">
              {{ number.did }}
            </h6>
            <div class="mt-2 flex flex-wrap items-center gap-2">
              <span class="rounded-full px-3 py-1 text-xs font-semibold" :class="registrationRequired ? 'bg-[#FFF4D8] text-[#8C6400]' : 'bg-[#E7F6EE] text-[#1D6B4F]'">
                {{ registrationRequired ? 'Registration Needed' : 'Ready to Subscribe' }}
              </span>
              <span v-if="registrationRequired && registration" class="rounded-full bg-[#E8F5EE] px-3 py-1 text-xs font-semibold text-[#1D6B4F]">
                {{ identityName }}
              </span>
            </div>
          </div>
        </div>

        <div class="mt-4 flex items-center text-sm font-medium text-neutral-400 dark:text-slate-400 font-manrope">
          <PiClockAfternoonLight class="mr-2 size-5" />
          <transition name="fade">
            <span v-if="remainingTime">Reservation expires in {{ remainingTime }}</span>
          </transition>
        </div>
      </div>

      <div class="flex w-full flex-col gap-3 lg:w-auto lg:min-w-[280px]">
        <Button
          v-if="registrationRequired && !registration"
          variant="danger"
          title="Attach Registration"
          size="md"
          class="w-full rounded-[12px] border-0 bg-[#FFF1F1] py-3 text-[#A12D2D]"
          @click="emitAttachRegisration"
        />
        <button
          v-else-if="registrationRequired && registration"
          type="button"
          class="flex w-full items-center justify-between rounded-[12px] bg-[#E8F5EE] px-4 py-3 text-left text-sm font-semibold text-[#1D6B4F] font-manrope"
          @click="emitAttachRegisration"
        >
          <span class="truncate pr-3">{{ identityName }}</span>
          <span class="shrink-0 text-red-500 hover:opacity-75" @click.stop="emitDetachRegistration">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M10 1a9 9 0 100 18 9 9 0 000-18zm4.95 12.95a.75.75 0 01-1.06 1.06L10 11.06l-3.89 3.89a.75.75 0 11-1.06-1.06L8.94 10 5.05 6.11a.75.75 0 111.06-1.06L10 8.94l3.89-3.89a.75.75 0 111.06 1.06L11.06 10l3.89 3.89z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
        </button>
        <Button
          variant="primary"
          title="Subscribe"
          size="md"
          :disabled="registrationRequired && !registration"
          class="w-full rounded-[12px] py-3"
          @click="emitSubscribe"
        >
          {{ isProcessingOrder ? 'Processing...' : 'Subscribe' }}
        </Button>
        <Button
          variant="secondary"
          title="Cancel Reservation"
          size="md"
          class="w-full rounded-[12px] py-3"
          @click="emitCancel"
        />
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import Button from "~/components/common/Button.vue";
import { PiClockAfternoonLight } from "vue3-icons/pi";
import { DateTime } from "luxon";
import type {
  BusinessIdentity, 
  PersonalIdentity, 
  NumberRegistration 
} from "@/types/numberRegistration";

const props = defineProps({
  number: {
    type: Object,
    required: true
  },
  registrationRequired: {
    type: Boolean,
    required: false
  },
  registration: {
    type: Object as PropType<NumberRegistration>,
    required: false
  },
  isProcessingOrder: {
    type: Boolean,
    required: false
  }
});

const emit = defineEmits(['cancel', 'expired', 'attachRegistration', 'detachRegistration', 'startSubscribe']);

const remainingTime = ref('');


const emitCancel = () => {
  emit('cancel');
};

const emitExpired = () => {
  emit('expired');
};

const emitAttachRegisration = () => {
  emit('attachRegistration');
};

const emitDetachRegistration = () => {
  emit('detachRegistration');
};

const emitSubscribe = () => {
  emit('startSubscribe');
};

let interval: NodeJS.Timeout;

// Use Nuxt's onMounted and onUnmounted hooks
// start counting up every second
onMounted(() => {
  if (import.meta.client) {
    remainingTime.value = getRemainingTime();
    interval = setInterval(() => {
      remainingTime.value = getRemainingTime();
    }, 1000);
    onUnmounted(() => {
      clearInterval(interval);
    });
  }
});

const getRemainingTime = () => {
  const now = DateTime.now();
  const expiresAt = DateTime.fromISO(props.number.reservation.endAt);
  let { hours, minutes, seconds } = expiresAt.diff(now, ['hours', 'minutes', 'seconds']);
  if (now > expiresAt) {
    // emit expired event
    emitExpired();
    // stop the interval
    clearInterval(interval);
    return 'Expired';
  }
  // units to the nearest whole number
  seconds = Math.round(seconds);
  return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : (minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`);
};

const identityName = computed(() => {
  if (props.registration) {
    return props.registration.identityType === 'business' ? 
    (props.registration.identity as BusinessIdentity).companyName : 
    (props.registration.identity as PersonalIdentity).firstName + ' ' + (props.registration.identity as PersonalIdentity).lastName; 
  }
  return '';
});

const numberInitials = computed(() => {
  return props.number.did.slice(0, 2).toUpperCase();
});

</script>

<style scoped>
.fade-enter-active {
  transition: opacity 4s ease-in;
}

.fade-enter-from {
  opacity: 0;
}

.fade-enter-to {
  opacity: 1;
}
</style>